import React from 'react'
import Inner from '../component/Innerbanner' // Assuming Innerbanner is in this location

// A helper component to create styled sections
const PolicySection = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-2xl font-bold text-gray-900 mb-3">{title}</h2>
    <div className="space-y-4 text-gray-500">
      {children}
    </div>
  </div>
);

// A helper component for list items
const ListItem = ({ children }) => (
  <li className="ml-5 list-disc">{children}</li>
);

function PrivacyPolicy() {
  return (
    <div className="font-custom">
      <Inner name="Privacy Policy" title="Home . Privacy Policy" />

      {/* --- Privacy Policy Content --- */}
      <div className="bg-white py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          <PolicySection title="Our Commitment To Privacy">
            <p>
              Your privacy is very important to us. To better protect your privacy, we provide this notice explaining our online information practices and the choices you can make about the way your information is collected and used.
            </p>
          </PolicySection>

          <PolicySection title="Information That We Collect">
            <p>
              UberPrints collects information to provide the goods and services you request and to improve your online shopping experience. The information that we collect is generally the information that you share with us to create an online account or to process your order. This may include your name, email address, telephone number, billing and shipping addresses and payment information.
            </p>
            <p>
              This may also include information you provide us through technology, such as through a cookie placed on your computer when you visit our website. This includes information such as your Internet Protocol (IP) address, the type of device you use, your device operating system and browser type, a unique device identifier, the address of referring websites, the path you take through our website, and other information about your session on our website.
            </p>
            <p>
              Other identifying information you may provide to us includes photos and images that you upload to UberPrints.com when creating designs; and tags, posts, comments, reviews, shared images or photos that you provide to UberPrints.com or that you post on social media platforms promoting UberPrints products or services.
            </p>
          </PolicySection>

          <PolicySection title="How We Use Your Information">
            <ul>
              <ListItem>
                <strong>Order Processing:</strong> The information you provide about yourself when placing an order is used only to complete that order. We do not share this information with outside parties except to the extent necessary to complete that order. Any images, photos, logos, quotes or other uploaded material are used only to produce or process your order.
              </ListItem>
              <ListItem>
                <strong>Communication:</strong> We use return email addresses to answer the email we receive, and phone numbers provided to reconcile questions regarding your order.
              </ListItem>
              <ListItem>
                <strong>Marketing and Promotions:</strong> We may use your information to provide UberPrints promotions, discounts and other updates through email, third party advertising firms and through UberPrints.com.
              </ListItem>
            </ul>
          </PolicySection>

          <PolicySection title="With Whom Your Information Is Shared">
            <ul>
              <ListItem>
                <strong>Contractors, Vendors and Service Providers:</strong> Your information is only shared to the extent necessary to process your order. This may include providing your information to contractors, vendors and service providers that provide services on UberPrints' behalf including shipping vendors and payment processors. We require our service providers keep your personal information private and secure. We do not allow our service providers to use or share your personal information for any purpose other than providing services on our behalf.
              </ListItem>
              <ListItem>
                <strong>Legal Requirements and Protection of Third Party Rights:</strong> We may share your personal information in other circumstances, which include situations when sharing is required by law, or we believe sharing will help to protect the safety, property, or rights of others. This may include identifying and addressing fraud, enforcing trademark and copyright laws, or responding to an investigative body in the case of a breach of an agreement or violation of law.
              </ListItem>
              <ListItem>
                <strong>Business reorganization, transfer, & subsidiaries:</strong> We may share your information with current or future subsidiaries or division of UberPrints, Inc. If at any time UberPrints is merged, sold or otherwise reorganized or restructured, your information may be shared with the successor business.
              </ListItem>
            </ul>
          </PolicySection>

          <PolicySection title="Cookies & Similar Technologies">
            <p>
              Cookies are small text files placed on your device to store data that can be recalled by a web server in the domain that placed the cookie. UberPrints uses cookies for storing your preferences and settings, storing information about your session on our website and for allowing you to stay signed in to your account. Because of this, you must set your browser to accept cookies if it does not already.
            </p>
            <p>
              We do not store any personal or secure information in the cookie. Controls in your internet browser can be used to limit how the websites you visit are able to use cookies and you can withdraw consent by deleting or blocking cookies.
            </p>
          </PolicySection>

          <PolicySection title="How We Protect Your Information">
            <p>
              We are committed to protecting the information we collect and we have implemented appropriate security procedures to help protect the personal information you provide to us.
            </p>
            <p>
              Examples of security measures we use to protect your personal information are:
            </p>
            <ul>
              <ListItem>
                <strong>Passwords:</strong> A password is required to access your UberPrints.com online account to protect your account information. Please keep this password confidential.
              </ListItem>
              <ListItem>
                <strong>Encryption technology:</strong> UberPrints uses Secure Sockets Layer (SSL) encryption to protect personal information when transmitting between your system and ours across the Internet. The presence of SSL encryption may be indicated by https in the browser URL or the image of a closed lock or solid key in the browser window. These indications may not be present in mobile services that use SSL.
              </ListItem>
              <ListItem>
                <strong>Tokenization:</strong> Credit card numbers never pass through or are stored on UberPrints servers. For payment card information we use secured networks and tokenization to protect cardholder data and information through the PayPal/Braintree payment gateway which ensures we are Payment Card Industry Data Security Standard (PCI DSS) compliant.
              </ListItem>
            </ul>
          </PolicySection>
          
          <PolicySection title="Your rights under the General Data Protection Regulation (GDPR)">
            <p>
              If you are visiting our website from the EEA or an EU country you have the following rights under the GDPR. Some of these rights are complex and not all of the details have been included in our summaries. Please read the laws and guidance from the regulatory authorities about General Data Drotection Regulation (GDPR) (EU) 2016/679 for a full explanation of these rights:
            </p>
            <ul>
              <ListItem><strong>Right to Access:</strong> You have the right to confirm whether we have collected your personal information and, if we have, you have the right access your personal information and receive a copy of that information.</ListItem>
              <ListItem><strong>Right to Rectification:</strong> You have the right to have any inaccurate data about you completed, corrected or updated if the information we have is incomplete, incorrect or out of date.</ListItem>
              <ListItem><strong>Right to Erasure:</strong> In some cases you have the right have your personal data erased without delay.</ListItem>
              <ListItem><strong>Right to Restrict Processing:</strong> In some cases you have the right to restrict the processing of your data.</ListItem>
              <ListItem><strong>Right to Object to Processing:</strong> In some cases you have the right to object to our processing of your personal data.</ListItem>
              <ListItem><strong>Right to Data Portability:</strong> You have the right to receive your personal data from us in a structured, commonly used format that can be used to transfer your information to another party.</ListItem>
              <ListItem><strong>Right to Withdraw Consent:</strong> In instances where the legal basis for our storage or processing of your personal information is consent, you have the right to withdraw that consent at any time you see fit.</ListItem>
            </ul>
          </PolicySection>

          <PolicySection title="How To Contact Us">
            <p>
              Should you have other questions or concerns about these privacy policies, please call us at 1-866-440-8237 or email us. Please check our privacy policy periodically for changes.
            </p>
          </PolicySection>

        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy